/**
 * Class page native share overlay: bundled with application.js so it is not blocked
 * by CSP on inline scripts. Uses bubble-phase click on open triggers.
 * Supports centered modal (inline Share), bottom sheet (mobile More → Share), and
 * presentation "responsive" (sheet ≤767px, modal above) for series page inline Share.
 */

const BOUND_ATTR = 'data-class-pdp-share-bound'
const OPEN_BTN_BOUND = 'data-class-pdp-share-open-btn-bound'

/** GTM dataLayer: event name from `data-share-event-name` (default class_social_share). */
function pushClassPdpShareDataLayer(root, destination) {
  try {
    window.dataLayer = window.dataLayer || []
    const title = root.getAttribute('data-share-title') || ''
    const shareUrl = root.getAttribute('data-share-url') || ''
    const presentation =
      root.getAttribute('data-class-pdp-share-last-presentation') || ''
    const shareSource =
      root.getAttribute('data-share-source') || 'class_page_action_bar'
    const eventName =
      root.getAttribute('data-share-event-name') || 'class_social_share'
    window.dataLayer.push({
      event: eventName,
      destination,
      share_source: shareSource,
      content_title: title,
      share_url: shareUrl,
      share_presentation: presentation
    })
  } catch (e) {
    /* non-fatal */
  }
}

function getOpenButtonsForRoot(root) {
  const id = root.id
  if (!id) return []
  const target = `#${id}`
  return Array.from(
    document.querySelectorAll('[data-class-pdp-share-open]')
  ).filter((btn) => btn.getAttribute('data-class-pdp-share-target') === target)
}

function appendUtm(url, source) {
  try {
    const u = new URL(url, window.location.href)
    u.searchParams.set('utm_source', source)
    u.searchParams.set('utm_medium', 'social_share')
    u.searchParams.set('utm_campaign', 'content_page')
    return u.toString()
  } catch (e) {
    return url
  }
}

function shareLine(title, kind) {
  if (kind === 'series') {
    return `Check out the program "${title}" on ALO Wellness Club`
  }
  return `Check out "${title}" on ALO Wellness Club`
}

function writeClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text)
  }
  return new Promise((resolve, reject) => {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    try {
      document.execCommand('copy')
      resolve()
    } catch (err) {
      reject(err)
    } finally {
      document.body.removeChild(ta)
    }
  })
}

function applyRootLayout(root, presentation) {
  const backdrop = root.querySelector('.class-pdp-share-native__backdrop')
  const panel = root.querySelector('.class-pdp-share-native__panel')
  if (backdrop) {
    backdrop.style.cssText =
      'position:absolute;inset:0;background:rgba(0,0,0,0.5);cursor:pointer;'
  }
  if (panel) {
    if (presentation === 'sheet') {
      panel.style.cssText =
        'position:relative;z-index:1;width:100%;max-width:100%;background:#fff;border-radius:8px 8px 0 0;box-shadow:0 -4px 24px rgba(0,0,0,0.12);overflow:hidden;'
    } else {
      panel.style.cssText =
        'position:relative;z-index:1;width:100%;max-width:496px;background:#fff;border-radius:8px;box-shadow:0 4px 12px rgba(35,41,54,0.08),0 2px 6px rgba(0,0,0,0.06);overflow:hidden;'
    }
  }
}

function showRoot(root, presentation) {
  root.setAttribute('aria-hidden', 'false')
  if (presentation === 'sheet') {
    root.style.cssText = [
      'display:flex!important',
      'position:fixed!important',
      'top:0!important',
      'left:0!important',
      'right:0!important',
      'bottom:0!important',
      'width:100%!important',
      'height:100%!important',
      'z-index:2147483646!important',
      'align-items:flex-end!important',
      'justify-content:center!important',
      'padding:0!important',
      'padding-bottom:env(safe-area-inset-bottom,0)!important',
      'box-sizing:border-box!important',
      'margin:0!important'
    ].join('')
  } else {
    root.style.cssText = [
      'display:flex!important',
      'position:fixed!important',
      'top:0!important',
      'left:0!important',
      'right:0!important',
      'bottom:0!important',
      'width:100%!important',
      'height:100%!important',
      'z-index:2147483646!important',
      'align-items:center!important',
      'justify-content:center!important',
      'padding:16px!important',
      'box-sizing:border-box!important',
      'margin:0!important'
    ].join('')
  }
  applyRootLayout(root, presentation)
}

function hideRoot(root) {
  root.setAttribute('aria-hidden', 'true')
  /* Reset full inline style so fixed overlay does not block clicks when closed */
  root.style.cssText = 'display:none!important'
}

/** @type {WeakMap<Element, { openOverlay: Function, closeOverlay: Function }>} */
const rootControllers = new WeakMap()

function initShareRoot(root) {
  if (root.getAttribute(BOUND_ATTR) === '1') {
    return rootControllers.get(root)
  }
  root.setAttribute(BOUND_ATTR, '1')

  if (root.parentElement !== document.body) {
    document.body.appendChild(root)
  }

  applyRootLayout(root, 'modal')
  hideRoot(root)

  const copiedEl = root.querySelector('[data-class-pdp-share-copied]')
  let escapeHandler

  const setAllTriggersExpanded = (expanded, except) => {
    getOpenButtonsForRoot(root).forEach((b) => {
      if (expanded && b === except) {
        b.setAttribute('aria-expanded', 'true')
      } else {
        b.setAttribute('aria-expanded', 'false')
      }
    })
  }

  const closeOverlay = () => {
    hideRoot(root)
    root.classList.remove('class-pdp-share-native__root--sheet')
    setAllTriggersExpanded(false)
    document.body.style.overflow = ''
    if (escapeHandler) {
      document.removeEventListener('keydown', escapeHandler)
      escapeHandler = null
    }
    const copyBtn = root.querySelector('[data-share-action="copy"]')
    const copyLabel = copyBtn?.querySelector('.class-pdp-share-native__label')
    if (copyLabel) copyLabel.textContent = 'Copy Link'
    if (copiedEl) copiedEl.hidden = true
  }

  const openOverlay = (e, sourceBtn, presentation) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    const mode = presentation === 'sheet' ? 'sheet' : 'modal'
    if (mode === 'sheet') {
      root.classList.add('class-pdp-share-native__root--sheet')
    } else {
      root.classList.remove('class-pdp-share-native__root--sheet')
    }
    showRoot(root, mode)
    root.setAttribute('data-class-pdp-share-last-presentation', mode)
    setAllTriggersExpanded(true, sourceBtn)
    document.body.style.overflow = 'hidden'
    escapeHandler = (ev) => {
      if (ev.key === 'Escape') closeOverlay()
    }
    document.addEventListener('keydown', escapeHandler)
  }

  root.querySelectorAll('[data-class-pdp-share-close]').forEach((btn) => {
    btn.addEventListener('click', (ev) => {
      ev.preventDefault()
      closeOverlay()
    })
  })

  root.querySelectorAll('[data-share-action]').forEach((btn) => {
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation()
      const url = root.getAttribute('data-share-url') || ''
      const title = root.getAttribute('data-share-title') || ''
      const kind = root.getAttribute('data-share-kind') || 'class'
      const line = shareLine(title, kind)
      const emailSubject =
        root.getAttribute('data-email-subject') ||
        'Check out this class on ALO Wellness Club'
      const action = btn.getAttribute('data-share-action')

      if (action === 'facebook') {
        pushClassPdpShareDataLayer(root, 'facebook')
        const u = appendUtm(url, 'facebook')
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            u
          )}`,
          'facebook-share-dialog',
          'width=626,height=436,noopener,noreferrer'
        )
        closeOverlay()
        return
      }
      if (action === 'message') {
        pushClassPdpShareDataLayer(root, 'message')
        const uSms = appendUtm(url, 'sms')
        window.location.href = `sms:?&body=${encodeURIComponent(
          `${line} ${uSms}`
        )}`
        closeOverlay()
        return
      }
      if (action === 'email') {
        pushClassPdpShareDataLayer(root, 'email')
        const uMail = appendUtm(url, 'email')
        window.location.href = `mailto:?subject=${encodeURIComponent(
          emailSubject
        )}&body=${encodeURIComponent(`${line} ${uMail}`)}`
        closeOverlay()
        return
      }
      if (action === 'copy') {
        pushClassPdpShareDataLayer(root, 'copy_link')
        const uCopy = appendUtm(url, 'copy_link')
        writeClipboard(`${line} ${uCopy}`)
          .then(() => {
            if (copiedEl) copiedEl.hidden = false
            const label = btn.querySelector('.class-pdp-share-native__label')
            if (label) label.textContent = 'Copied!'
            setTimeout(() => {
              if (label) label.textContent = 'Copy Link'
              if (copiedEl) copiedEl.hidden = true
            }, 2000)
          })
          .catch(() => {})
      }
    })
  })

  const controller = { openOverlay, closeOverlay }
  rootControllers.set(root, controller)
  return controller
}

function bindOpenButton(root, openBtn) {
  if (openBtn.getAttribute(OPEN_BTN_BOUND) === '1') return
  openBtn.setAttribute(OPEN_BTN_BOUND, '1')

  const controller = initShareRoot(root)
  if (!controller) return

  openBtn.addEventListener('click', (e) => {
    let presentation =
      openBtn.getAttribute('data-class-pdp-share-presentation') || 'modal'
    if (presentation === 'responsive') {
      presentation = window.matchMedia('(max-width: 767px)').matches
        ? 'sheet'
        : 'modal'
    }
    controller.openOverlay(e, openBtn, presentation)
  })
}

function scan() {
  document
    .querySelectorAll('[data-class-pdp-share-open]')
    .forEach((openBtn) => {
      const sel = openBtn.getAttribute('data-class-pdp-share-target')
      if (!sel) return
      const root = document.querySelector(sel)
      if (!root) return
      bindOpenButton(root, openBtn)
    })
}

function run() {
  scan()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run)
} else {
  run()
}

document.addEventListener('turbo:load', run)
document.addEventListener('turbolinks:load', run)
